import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import notificationService from "../../services/notificationService";

interface Notification {
  id: number;
  title: string;
  message: string;
  type: string;
  priority: string;
  metadata?: any;
  sent_at: string;
  expires_at?: string;
  is_global: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  read?: boolean;
  dismissed?: boolean;
  read_at?: string;
}

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  stats: any;
  isLoading: boolean;
  error: string | null;
}

const initialState: NotificationState = {
  notifications: [],
  unreadCount: 0,
  stats: null,
  isLoading: false,
  error: null,
};

// Fixed: Using default parameter value
export const fetchUserNotifications = createAsyncThunk(
  "notifications/fetchUserNotifications",
  async (params: any = {}, { rejectWithValue }) => {
    try {
      const response = await notificationService.getUserNotifications(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch notifications"
      );
    }
  }
);

export const fetchNotificationStats = createAsyncThunk(
  "notifications/fetchStats",
  async (_, { rejectWithValue }) => {
    try {
      const response = await notificationService.getNotificationStats();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch notification stats"
      );
    }
  }
);

export const markAsRead = createAsyncThunk(
  "notifications/markAsRead",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await notificationService.markAsRead(id);
      return { id, ...response };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to mark as read"
      );
    }
  }
);

export const markAllAsRead = createAsyncThunk(
  "notifications/markAllAsRead",
  async (_, { rejectWithValue }) => {
    try {
      const response = await notificationService.markAllAsRead();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to mark all as read"
      );
    }
  }
);

export const dismissNotification = createAsyncThunk(
  "notifications/dismiss",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await notificationService.dismissNotification(id);
      return { id, ...response };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to dismiss notification"
      );
    }
  }
);

// Fixed: Using default parameter value
export const sendTestNotification = createAsyncThunk(
  "notifications/sendTest",
  async (message: string = "", { rejectWithValue }) => {
    try {
      const response = await notificationService.sendTestNotification(message);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to send test notification"
      );
    }
  }
);

const notificationSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserNotifications.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchUserNotifications.fulfilled, (state, action) => {
        state.isLoading = false;
        state.notifications = action.payload.data || [];
        state.unreadCount = action.payload.unread_count || 0;
      })
      .addCase(fetchUserNotifications.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchNotificationStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      })

      .addCase(markAsRead.fulfilled, (state, action) => {
        const id = action.meta.arg;
        const notification = state.notifications.find((n) => n.id === id);
        if (notification) {
          notification.read = true;
          notification.read_at = new Date().toISOString();
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      })

      .addCase(markAllAsRead.fulfilled, (state) => {
        state.notifications = state.notifications.map((notification) => ({
          ...notification,
          read: true,
          read_at: notification.read_at || new Date().toISOString(),
        }));
        state.unreadCount = 0;
      })

      .addCase(dismissNotification.fulfilled, (state, action) => {
        const id = action.meta.arg;
        state.notifications = state.notifications.filter(
          (notification) => notification.id !== id
        );
        // Update unread count if the dismissed notification was unread
        const dismissed = state.notifications.find((n) => n.id === id);
        if (dismissed && !dismissed.read) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      });
  },
});

export const { clearError } = notificationSlice.actions;
export default notificationSlice.reducer;
